/**
 * The device's explicit language choice, in localStorage (REQ-04). Anonymous players need no
 * account for it, and it survives sessions.
 *
 * Only an EXPLICIT pick is ever written: browser detection is recomputed on every visit and
 * never stored, which is what lets a `languagechange` move an undecided player and never a
 * decided one (REQ-05). Storage that throws (private mode, blocked site data) behaves as
 * "nothing stored"; a value that is not ours is removed.
 */
import type { StoredLocaleChoice } from "@shared/i18n/localePreference";

export const LOCALE_STORAGE_KEY = "suikamon:locale";

/** Longest plausible BCP 47 tag we would ever store; anything longer is not ours. */
const MAX_STORED_TAG_LENGTH = 35;

function isStoredChoice(value: unknown): value is StoredLocaleChoice {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.locale === "string" &&
    v.locale.length > 0 &&
    v.locale.length <= MAX_STORED_TAG_LENGTH &&
    typeof v.at === "number" &&
    Number.isFinite(v.at)
  );
}

export function readStoredLocaleChoice(): StoredLocaleChoice | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isStoredChoice(parsed)) return { locale: parsed.locale, at: parsed.at };
  } catch {
    // fall through: not JSON, so not ours
  }
  clearStoredLocaleChoice();
  return null;
}

export function writeStoredLocaleChoice(locale: string, at: number = Date.now()): StoredLocaleChoice {
  const choice = { locale, at };
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, JSON.stringify(choice));
  } catch {
    // Storage unavailable: the choice still applies for this session.
  }
  return choice;
}

export function clearStoredLocaleChoice(): void {
  try {
    window.localStorage.removeItem(LOCALE_STORAGE_KEY);
  } catch {
    // nothing to clear
  }
}
